import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ChannelBadge as RncChannelBadge } from "@/pages/signals/rnc/RncShared";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Check, Sparkles } from "lucide-react";
import { useNodesState, useEdgesState } from "reactflow";
import { outreachApi, signalsApi } from "@/lib/api";
import {
  defaultWorkflow,
  findDisconnectedWorkflowSteps,
  findMissingGoalNode,
  findMultipleGoalNodes,
  findWorkflowCycleIssues,
  formatWorkflowInvalidError,
  stripWorkflowUiState,
} from "@/lib/campaignWorkflow";
import {
  CAMPAIGN_NAME_MAX_LEN,
  displayCampaignName,
  validateCampaignName,
} from "@/lib/campaignName";
import { WorkflowCanvas } from "@/pages/outbound/WorkflowCanvas";
import { WorkflowPalette } from "@/pages/outbound/WorkflowPalette";
import { AskAgentSequenceModal } from "@/pages/outbound/AskAgentSequenceModal";
import { TimezoneInlineLabel, TimezoneSelect } from "@/components/common/TimezoneSelect";
import { DatePicker } from "@/components/common/DatePicker";
import { useAssistantName } from "@/hooks/useAssistantName";
import {
  ACTIVE_SIGNAL_CONFLICT_MESSAGE,
  ACTIVE_SIGNAL_SOURCES,
  COMING_SOON_MESSAGE,
  hasActiveSignalConflict,
  isActiveSignalSource,
} from "@/lib/campaignSignals";
import {
  FALLBACK_DEFAULT_CAMPAIGN_TIMEZONE,
  useCampaignTimezoneCatalog,
} from "@/hooks/useCampaignTimezoneCatalog";

const STEPS = [
  { id: "details", label: "Campaign details" },
  { id: "signals", label: "Signals" },
  { id: "workflow", label: "Workflow" },
  { id: "schedule", label: "Schedule" },
  { id: "review", label: "Review" },
];

function toggleSelection(list, key) {
  return list.includes(key) ? list.filter((c) => c !== key) : [...list, key];
}

function wizardStepClass(stepIndex, currentStep) {
  if (stepIndex === currentStep) return "bg-indigo-600 text-white ring-indigo-600";
  if (stepIndex < currentStep) return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  return "bg-slate-50 text-slate-500 ring-slate-200";
}

function clampStep(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(STEPS.length - 1, Math.floor(n)));
}

function defaultSchedule(timezone = FALLBACK_DEFAULT_CAMPAIGN_TIMEZONE) {
  return {
    start_date: "",
    timezone,
  };
}

function emptyForm(timezone = FALLBACK_DEFAULT_CAMPAIGN_TIMEZONE) {
  return {
    name: "",
    description: "",
    template: "Custom",
    signalSources: [],
    audience: { segment: "", notes: "" },
    schedule: defaultSchedule(timezone),
  };
}

/** Map wizard API DTO → local form state. */
function formFromWizardDto(dto, defaultTimezone = FALLBACK_DEFAULT_CAMPAIGN_TIMEZONE) {
  const details = dto?.details || {};
  const schedule = dto?.schedule || {};
  return {
    name: details.name || "",
    description: details.description || "",
    template: details.template || "Custom",
    signalSources: Array.isArray(dto?.signalSources)
      ? dto.signalSources.filter((k) => isActiveSignalSource(k))
      : [],
    audience: {
      segment: dto?.audience?.segment || "",
      notes: dto?.audience?.notes || "",
    },
    schedule: {
      start_date: schedule.start_date || "",
      timezone: schedule.timezone || defaultTimezone,
    },
  };
}

function scheduleTimezoneOk(schedule) {
  return Boolean((schedule?.timezone || "").trim());
}

export function CampaignWizard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const agentName = useAssistantName();
  const { defaultIana } = useCampaignTimezoneCatalog();
  const resumeId = searchParams.get("id") || null;

  const [campaignId, setCampaignId] = useState(resumeId);
  const [hydrating, setHydrating] = useState(Boolean(resumeId));
  const [step, setStep] = useState(0);
  const [savingAction, setSavingAction] = useState(null); // null | "save" | "advance" | "complete"
  const saving = savingAction != null;
  const [nameConflict, setNameConflict] = useState(null);
  const [askOpen, setAskOpen] = useState(false);
  const [askGenerating, setAskGenerating] = useState(false);
  const [signalOptions, setSignalOptions] = useState([]);
  const [signalsLoading, setSignalsLoading] = useState(true);
  const [signalsError, setSignalsError] = useState(null);
  const [form, setForm] = useState(() => emptyForm());
  const wf = defaultWorkflow(["email"]);
  const [nodes, setNodes, onNodesChange] = useNodesState(wf.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(wf.edges);

  const setWorkflow = ({ nodes: n, edges: e }) => {
    setNodes(n);
    setEdges(e);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSignalsLoading(true);
      setSignalsError(null);
      try {
        const data = await signalsApi.listCatalog();
        if (cancelled) return;
        const sources = Array.isArray(data?.sources) ? data.sources : [];
        setSignalOptions(sources);
        if (!sources.length) {
          setSignalsError("No Signal sources are configured for this workspace yet.");
        }
      } catch (e) {
        if (!cancelled) setSignalsError(e.message || "Failed to load Signal sources");
      } finally {
        if (!cancelled) setSignalsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!resumeId) {
      setHydrating(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setHydrating(true);
      try {
        const dto = await outreachApi.getWizard(resumeId);
        if (cancelled) return;
        setCampaignId(dto.campaignId);
        setForm(formFromWizardDto(dto, defaultIana));
        const workflow = dto.workflow || {};
        const fallback = defaultWorkflow(["email"]);
        setNodes(workflow.nodes?.length ? workflow.nodes : fallback.nodes);
        setEdges(workflow.edges?.length ? workflow.edges : fallback.edges);
        setStep(clampStep(dto.step));
      } catch (e) {
        if (cancelled) return;
        if (e.code === "wizard_complete") {
          navigate(`/outreach/campaigns/${resumeId}/edit`, { replace: true });
          return;
        }
        toast.error(e.message || "Could not resume campaign");
        navigate("/outreach/campaigns/new", { replace: true });
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [resumeId, navigate, setNodes, setEdges, defaultIana]);

  const handleNodeDropped = (newNode) => {
    toast.success(`Added ${newNode.data?.label || "step"}`);
  };

  const toggleSignal = async (key) => {
    if (!isActiveSignalSource(key)) {
      toast.message(COMING_SOON_MESSAGE);
      return;
    }
    const next = toggleSelection(form.signalSources, key);
    // Selecting a Signal that another active campaign already owns is blocked.
    if (next.includes(key) && !form.signalSources.includes(key)) {
      try {
        if (await hasActiveSignalConflict(outreachApi, [key], campaignId)) {
          toast.error(ACTIVE_SIGNAL_CONFLICT_MESSAGE);
          return;
        }
      } catch (e) {
        toast.error(e?.message || ACTIVE_SIGNAL_CONFLICT_MESSAGE);
        return;
      }
    }
    setForm((f) => ({ ...f, signalSources: next }));
  };

  const nameTrimmed = form.name.trim();
  const nameValidation = validateCampaignName(form.name);
  const nameLengthError =
    nameTrimmed.length > 0 && !nameValidation.ok ? nameValidation.error : null;
  const nameError = nameLengthError || nameConflict;

  const canNext = () => {
    if (step === 0) return nameValidation.ok;
    if (step === 1) {
      return form.signalSources.some((k) => ACTIVE_SIGNAL_SOURCES.has(k));
    }
    if (step === 3) return scheduleTimezoneOk(form.schedule);
    return true;
  };

  const canSave = () => {
    if (step === 0) return nameValidation.ok;
    if (step === 3) return scheduleTimezoneOk(form.schedule);
    return true;
  };

  const _applyWizardDto = (dto) => {
    if (dto.campaignId) {
      setCampaignId(dto.campaignId);
      if (!searchParams.get("id")) {
        setSearchParams({ id: dto.campaignId }, { replace: true });
      }
    }
  };

  const _checkSignalConflict = async (allowedSources) => {
    if (!allowedSources.length) return true;
    try {
      if (await hasActiveSignalConflict(outreachApi, allowedSources, campaignId)) {
        toast.error(ACTIVE_SIGNAL_CONFLICT_MESSAGE);
        return false;
      }
    } catch (e) {
      toast.error(e?.message || ACTIVE_SIGNAL_CONFLICT_MESSAGE);
      return false;
    }
    return true;
  };

  const _assertWorkflowConnected = () => {
    const missingGoal = findMissingGoalNode(nodes);
    if (missingGoal.length) {
      if (step !== 2) setStep(2);
      toast.error(missingGoal[0].message);
      return false;
    }
    const multipleGoals = findMultipleGoalNodes(nodes);
    if (multipleGoals.length) {
      if (step !== 2) setStep(2);
      toast.error(multipleGoals[0].message);
      return false;
    }
    const disconnected = findDisconnectedWorkflowSteps(nodes, edges);
    if (disconnected.length) {
      if (step !== 2) setStep(2);
      toast.error(disconnected[0].message);
      return false;
    }
    const cycleIssues = findWorkflowCycleIssues(nodes, edges);
    if (cycleIssues.length) {
      if (step !== 2) setStep(2);
      toast.error(cycleIssues[0].message);
      return false;
    }
    return true;
  };

  const _validateBeforePersist = async (action, allowedSources) => {
    const checked = validateCampaignName(form.name);
    if (!checked.ok) {
      toast.error(checked.error);
      return { ok: false };
    }
    if (!scheduleTimezoneOk(form.schedule)) {
      toast.error("Select a timezone.");
      if (step !== 3) setStep(3);
      return { ok: false };
    }
    if (action === "complete" && !allowedSources.length) {
      toast.error("Select at least one available Signal source (RNC or ABM List)");
      return { ok: false };
    }
    // Goal / topology only matter once the user is on (or finishing from) the
    // Workflow step. Early Next/Save must not jump to Workflow with that error.
    const mustValidateWorkflow =
      action === "complete"
      || ((action === "advance" || action === "save") && step === 2);
    if (mustValidateWorkflow && !_assertWorkflowConnected()) {
      return { ok: false };
    }
    if (!(await _checkSignalConflict(allowedSources))) {
      return { ok: false };
    }
    return { ok: true, name: checked.name };
  };

  const _handlePersistError = (e) => {
    if (e.code === "name_conflict") {
      setNameConflict(e.message || "A campaign with this name already exists.\nCampaign names are case-insensitive.");
      if (step !== 0) setStep(0);
      return;
    }
    if (e.code === "signal_conflict") {
      toast.error(e.message || ACTIVE_SIGNAL_CONFLICT_MESSAGE);
      return;
    }
    if (e.code === "workflow_invalid") {
      if (step !== 2) setStep(2);
      toast.error(formatWorkflowInvalidError(e));
      return;
    }
    toast.error(e.message || "Failed to save campaign");
  };

  const persistWizard = async (action) => {
    const allowedSources = (form.signalSources || []).filter((k) => isActiveSignalSource(k));
    const valid = await _validateBeforePersist(action, allowedSources);
    if (!valid.ok) return null;

    setNameConflict(null);
    setSavingAction(action);
    try {
      const dto = await outreachApi.saveWizard({
        action,
        step,
        campaignId: campaignId || undefined,
        details: {
          name: valid.name,
          description: form.description.trim() || null,
          template: form.template,
        },
        signalSources: allowedSources,
        audience: form.audience,
        schedule: form.schedule,
        workflow: {
          nodes: stripWorkflowUiState(nodes),
          edges,
          name: valid.name,
        },
      });
      _applyWizardDto(dto);
      return dto;
    } catch (e) {
      _handlePersistError(e);
      return null;
    } finally {
      setSavingAction(null);
    }
  };

  const handleSave = async () => {
    if (!canSave()) return;
    const dto = await persistWizard("save");
    if (dto) toast.success("Progress saved");
  };

  const handleNext = async () => {
    if (!canNext()) return;
    const dto = await persistWizard("advance");
    if (!dto) return;
    setStep(clampStep(dto.step));
  };

  const handleFinish = async () => {
    const dto = await persistWizard("complete");
    if (!dto) return;
    toast.success("Campaign created successfully");
    navigate(`/outreach/campaigns/${dto.campaignId}/edit`, { state: { campaignCreated: true } });
  };

  const hasMeaningfulFlow = () => {
    const real = (nodes || []).filter((n) => (n.type || "").toLowerCase() !== "start");
    return real.length > 0;
  };

  const handleAskGenerate = async (payload) => {
    if (hasMeaningfulFlow()) {
      const ok = window.confirm(
        "Replace the current sequence on the canvas with the generated one?",
      );
      if (!ok) return;
    }
    const fromMaya = (payload?.signalSources || []).filter((k) => isActiveSignalSource(k));
    if (fromMaya.length) {
      try {
        if (await hasActiveSignalConflict(outreachApi, fromMaya, campaignId)) {
          toast.error(ACTIVE_SIGNAL_CONFLICT_MESSAGE);
          return;
        }
      } catch (e) {
        toast.error(e?.message || ACTIVE_SIGNAL_CONFLICT_MESSAGE);
        return;
      }
      setForm((f) => ({ ...f, signalSources: fromMaya }));
    }
    setAskGenerating(true);
    try {
      const result = await outreachApi.aiDesignSequencePreview(payload);
      const nextWf = result.workflow || { nodes: [], edges: [] };
      setNodes(nextWf.nodes || []);
      setEdges(nextWf.edges || []);
      setAskOpen(false);
      setStep(2);
      toast.success(`${agentName} drafted a sequence — review it on the Workflow step`);
      if (result.warnings?.length) {
        toast.message(`${result.warnings.length} design warning(s)`);
      }
    } catch (e) {
      toast.error(e.message || "Failed to design sequence");
    } finally {
      setAskGenerating(false);
    }
  };

  const signalLabel = (key) => {
    const found = signalOptions.find((s) => s.key === key);
    return found?.name || key;
  };

  if (hydrating) {
    return (
      <div className="flex flex-1 items-center justify-center text-[13px] text-slate-500" data-testid="wizard-hydrating">
        Loading saved progress…
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-w-0 overflow-hidden bg-slate-50/20">
      <PageHeader
        title="Create campaign"
        description="Set up your outreach campaign step by step. Save to keep progress; leaving without Save discards unsaved changes."
        breadcrumbs={["Outreach", "Campaigns", "New"]}
        actions={step === 2 ? (
          <Button
            size="sm"
            className="h-9 sm:h-8 bg-violet-600 hover:bg-violet-700 text-white"
            onClick={() => setAskOpen(true)}
            data-testid="wizard-ask-agent-design-sequence"
          >
            <Sparkles size={12} />
            Ask {agentName} to design a sequence
          </Button>
        ) : null}
      />
      <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-slate-200 bg-white shrink-0">
        <div className="flex flex-wrap gap-2">
          {STEPS.map((s, i) => (
            <div
              key={s.id}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 sm:py-1 text-[11.5px] font-medium ring-1 whitespace-nowrap",
                wizardStepClass(i, step),
              )}
            >
              {i < step ? <Check size={12} /> : <span>{i + 1}</span>}
              {s.label}
            </div>
          ))}
        </div>
      </div>

      <div className={cn(
        "flex-1 overflow-x-hidden scrollbar-thin",
        step === 2 ? "min-h-0 overflow-hidden flex flex-col" : "overflow-y-auto"
      )}>
        <div className={cn(
          "p-4 sm:p-6 max-w-5xl mx-auto w-full min-w-0 flex flex-col",
          step === 2 ? "flex-1 min-h-0" : "min-h-full"
        )}>
          {step === 0 && (
          <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-4 sm:p-6">
            <div>
              <Label className="text-[12px]">Campaign name</Label>
              <Input
                value={form.name}
                onChange={(e) => {
                  setNameConflict(null);
                  setForm((f) => ({ ...f, name: e.target.value }));
                }}
                placeholder="e.g. Q2 Enterprise Outbound"
                maxLength={CAMPAIGN_NAME_MAX_LEN}
                className="mt-1 h-11 sm:h-9 w-full min-w-0"
                data-testid="wizard-campaign-name"
                aria-invalid={Boolean(nameError)}
              />
              {nameError ? (
                <p className="mt-1 text-[12px] text-red-600 whitespace-pre-line" data-testid="wizard-campaign-name-error">
                  {nameError}
                </p>
              ) : (
                <p className="mt-1 text-[11px] text-slate-400">
                  {nameTrimmed.length}/{CAMPAIGN_NAME_MAX_LEN}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="wizard-campaign-description" className="text-[12px]">
                Description (optional)
              </Label>
              <Textarea
                id="wizard-campaign-description"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Provide a brief description of this campaign…"
                className="mt-1 min-h-[80px] w-full min-w-0"
                data-testid="wizard-campaign-description"
              />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6" data-testid="wizard-signals-step">
            <Label className="text-[12px]">Select Signal source(s)</Label>
            <p className="mt-1 text-[12px] text-slate-500">
              Choose which Signal sources will feed this campaign. Communication channels are configured in the workflow.
            </p>
            {signalsLoading && (
              <p className="mt-4 text-[13px] text-slate-500" data-testid="wizard-signals-loading">
                Loading Signal sources…
              </p>
            )}
            {!signalsLoading && signalsError && !signalOptions.length && (
              <p className="mt-4 text-[13px] text-amber-700" data-testid="wizard-signals-empty">
                {signalsError}
              </p>
            )}
            {!signalsLoading && signalOptions.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {signalOptions.map((src) => {
                  const key = src.key;
                  const active = form.signalSources.includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleSignal(key)}
                      className={cn(
                        "inline-flex items-center gap-2 rounded-lg border px-3 py-2.5 sm:py-2 text-[12px] font-medium min-h-11 sm:min-h-0",
                        active ? "border-indigo-400 bg-indigo-50 text-indigo-900" : "border-slate-200",
                      )}
                      data-testid={`wizard-signal-${key}`}
                    >
                      <RncChannelBadge channelKey={key} size={20} />
                      {src.name || key}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden flex flex-col md:grid md:grid-cols-[180px_1fr] flex-1 min-h-0">
            <WorkflowPalette
              className="border-b md:border-b-0 md:border-r border-slate-200 shrink-0 md:min-h-0"
              nodes={nodes}
            />
            <div className="relative h-[min(480px,52dvh)] min-h-[300px] sm:min-h-[400px] md:h-auto md:min-h-0 md:flex-1">
              <div className="absolute inset-0">
                <WorkflowCanvas
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onWorkflowChange={setWorkflow}
                  onAddNode={handleNodeDropped}
                />
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="mx-auto w-full max-w-md space-y-4 rounded-lg border border-slate-200 bg-white p-4 sm:p-6">
            <div>
              <Label className="text-[12px]">Start date (optional)</Label>
              <div className="mt-1">
                <DatePicker
                  value={form.schedule.start_date}
                  onChange={(dateStr) => setForm((f) => ({ ...f, schedule: { ...f.schedule, start_date: dateStr } }))}
                  placeholder="dd-mm-yyyy"
                  testId="wizard-start-date"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="wizard-timezone" className="text-[12px]">
                Timezone
              </Label>
              <TimezoneSelect
                id="wizard-timezone"
                testId="wizard-timezone"
                value={form.schedule.timezone}
                onChange={(timezone) => setForm((f) => ({ ...f, schedule: { ...f.schedule, timezone } }))}
              />
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="mx-auto w-full max-w-md rounded-lg border border-slate-200 bg-white p-4 sm:p-6 space-y-3 text-[13px] break-words">
            <div>
              <span className="text-slate-500">Name:</span>{" "}
              <span className="font-medium">{displayCampaignName(form.name)}</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-slate-500">Signals:</span>
              {form.signalSources.map((key) => (
                <span key={key} className="inline-flex items-center gap-1.5">
                  <RncChannelBadge channelKey={key} size={18} />
                  <span>{signalLabel(key)}</span>
                </span>
              ))}
            </div>
            <div><span className="text-slate-500">Workflow steps:</span> {nodes.length}</div>
            <div>
              <span className="text-slate-500">Schedule:</span>{" "}
              {form.schedule.start_date || "Start immediately"}
              {form.schedule.timezone ? (
                <>
                  {" · "}
                  <TimezoneInlineLabel iana={form.schedule.timezone} />
                </>
              ) : null}
            </div>
          </div>
        )}
        </div>
      </div>

      <div className="border-t border-slate-200 bg-white px-4 sm:px-6 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center justify-between gap-2 shrink-0">
        <Button
          variant="outline"
          size="sm"
          className="h-9 sm:h-8 w-auto"
          onClick={() => (step === 0 ? navigate("/outreach") : setStep((s) => s - 1))}
        >
          <ChevronLeft size={14} />
          {step === 0 ? "Cancel" : "Back"}
        </Button>
        <div className="flex flex-row gap-2 w-auto">
          <Button
            variant="outline"
            size="sm"
            className="h-9 sm:h-8 w-auto"
            disabled={!canSave() || saving}
            onClick={handleSave}
            data-testid="wizard-save"
          >
            {savingAction === "save" ? "Saving…" : "Save"}
          </Button>
          {step < STEPS.length - 1 ? (
            <Button
              size="sm"
              className="h-9 sm:h-8 w-auto"
              disabled={!canNext() || saving}
              onClick={handleNext}
              data-testid="wizard-next"
            >
              Next <ChevronRight size={14} />
            </Button>
          ) : (
            <Button
              size="sm"
              className="h-9 sm:h-8 w-auto"
              disabled={saving}
              onClick={handleFinish}
              data-testid="wizard-create-campaign"
            >
              {savingAction === "complete" ? "Creating…" : "Create campaign"}
            </Button>
          )}
        </div>
      </div>

      <AskAgentSequenceModal
        open={askOpen}
        agentName={agentName}
        generating={askGenerating}
        onOpenChange={setAskOpen}
        onGenerate={handleAskGenerate}
        initialSignalSources={form.signalSources}
      />
    </div>
  );
}
